import { AuctionEventStatusSchema, type AuctionEvent, type AuctionEventBranding, type AuctionEventStatus, type AuctionItem } from '@neon-one/auctions-types';

// ── Prototype-shaped types (subset — only what UI surfaces consume) ──
export type PrototypeAuctionStatus = 'draft' | 'scheduled' | 'live' | 'closed';
export type PrototypeAuctionFormat = 'silent';

export interface PrototypeAuction {
  id: string;
  slug: string;
  name: string;
  status: PrototypeAuctionStatus;
  // A paused auction has no countdown: its close time is chosen on resume.
  isPaused: boolean;
  // Archive is a flag beside status, never a status of its own: an archived
  // auction is always `closed` and reads `archivedAt` for when it went away.
  archivedAt: string | null;
  // True for an auction archived while published or open — it was closed
  // quietly with every item unsold, so a restore brings back no winners.
  closedWithoutSettlement: boolean;
  format: PrototypeAuctionFormat;
  eventLink?: string;
  description: string;
  startAt: string;
  endAt: string;
  coverImage?: string;
  branding: AuctionEventBranding | null;
  campaignId?: string | null;
  bidding: {
    defaultIncrement: number;
    perItemOverride: boolean;
    maxBid: boolean;
  };
  notifications: {
    channels: ('sms' | 'email')[];
    cadence: 'every-outbid' | 'batched';
  };
  closurePattern: 'auto-charge' | 'email-link' | 'invoice';
  items: number;
  bids: number;
  raised: number;
  bidders: number;
}

export interface PrototypeAuctionItem {
  id: string;
  auctionId: string;
  title: string;
  description: string;
  fmv: number;
  donors: { id: string; name: string }[];
  photo: string;
  photoBg: string;
  startingBid: number;
  increment: number;
  currentBid?: number;
  bidCount: number;
  topBidder?: string;
  status: 'no-bids' | 'active' | 'has-bids' | 'closed' | 'sold' | 'flagged';
  anonymous: boolean;
  bundleId?: string;
  isBundle?: boolean;
  hasUploadedPhoto?: boolean;
}

// ── Status mapping ────────────────────────────────────────────────────
// Eng's wire surface uses 'draft' | 'published' | 'open' | 'closed'. The
// prototype's UI buckets to 'draft' | 'scheduled' | 'live' | 'closed'.
// Mapping (total and lossless):
//   draft     → draft
//   published → scheduled  (published auctions that haven't opened yet)
//   open      → live
//   closed    → closed
export function toPrototypeStatus(s: AuctionEventStatus): PrototypeAuctionStatus {
  if (s === 'draft') return 'draft';
  if (s === 'published') return 'scheduled';
  if (s === 'open') return 'live';
  return 'closed';
}

// ── Currency ──────────────────────────────────────────────────────────
// Prototype shows dollars; eng stores cents. Centralized helper so the
// dollars/cents seam is consistent.
export const centsToDollars = (cents: number): number => cents / 100;
export const dollarsToCents = (dollars: number): number => Math.round(dollars * 100);

export function currency(value: number): string {
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

// ── Date helpers ──────────────────────────────────────────────────────
// Mirrors the prototype's parseAuctionDateMs — strips trailing tz hints
// the prototype embeds in date strings, then Date.parse. Eng's ISO-8601
// strings parse without the strip but the function tolerates both.
export function parseAuctionDateMs(s: string | null | undefined): number {
  if (!s) return NaN;
  const cleaned = s.replace(/\s+CT$|\s+ET$|\s+PT$|\s+MT$/, '');
  const t = Date.parse(cleaned);
  return Number.isFinite(t) ? t : NaN;
}

// ── Auction adapter ───────────────────────────────────────────────────
// Closed cards read Expected revenue and sold counts from the auction list.
// Live cards keep winning-bid counters from useHomepageLiveCounters.
// Other prototype-only fields retain the defaults documented in REVIEW.md.
export function toPrototypeAuction(event: AuctionEvent): PrototypeAuction {
  return {
    id: event.uuid,
    slug: event.slug,
    name: event.name,
    status: toPrototypeStatus(event.status),
    isPaused: event.isPaused,
    archivedAt: event.archivedAt ?? null,
    closedWithoutSettlement: event.closedWithoutSettlement ?? false,
    format: 'silent',
    description: event.descriptionHtml,
    startAt: event.auctionOpenAt,
    endAt: event.auctionCloseAt,
    coverImage: event.branding?.bannerUrl ?? undefined,
    branding: event.branding ?? null,
    campaignId: null, // eng surface doesn't carry campaignId yet — REVIEW item
    bidding: {
      defaultIncrement: 25, // hard-coded default; eng doesn't surface this on AuctionEvent
      perItemOverride: true, // canonical: always available; no auction-level toggle
      maxBid: true,
    },
    notifications: {
      channels: ['sms', 'email'],
      cadence: 'every-outbid',
    },
    closurePattern: event.autoCheckoutMode === 'auto_charge' ? 'auto-charge' : 'email-link',
    items: event.status === AuctionEventStatusSchema.enum.closed ? (event.itemsSoldCount ?? 0) : 0,
    bids: 0,
    raised: event.status === AuctionEventStatusSchema.enum.closed ? centsToDollars(event.expectedRevenueCents ?? 0) : 0,
    bidders: 0,
  };
}

// ── Item adapter ──────────────────────────────────────────────────────
// AuctionItem in eng is cents + numeric id + single donorId; prototype
// expects dollars + string id + donors[]. Donor lookups are not done
// here — UI shows the donor name only when the integration wires a
// donor-lookup composable. For now, donors[] is an empty array and the
// catalog shows the placeholder, see REVIEW item.
export function toPrototypeItem(item: AuctionItem): PrototypeAuctionItem {
  return {
    id: String(item.id),
    auctionId: String(item.auctionEventId),
    title: item.title,
    description: item.descriptionHtml,
    fmv: centsToDollars(item.fairMarketValueCents),
    donors: [], // wired via donor lookup later — REVIEW item
    photo: '',
    photoBg: 'sky',
    startingBid: centsToDollars(item.startingBidCents),
    increment: centsToDollars(item.bidIncrementCents),
    currentBid: item.currentHighBidCents > 0 ? centsToDollars(item.currentHighBidCents) : undefined,
    bidCount: 0, // eng's AuctionItem doesn't carry bidCount on the wire — REVIEW
    topBidder: undefined,
    status: mapItemStatus(item.status),
    anonymous: item.anonymousDonor,
    bundleId: undefined,
    isBundle: false,
    hasUploadedPhoto: item.photos.length > 0,
  };
}

function mapItemStatus(s: AuctionItem['status']): PrototypeAuctionItem['status'] {
  if (s === 'sold') return 'sold';
  if (s === 'withdrawn' || s === 'unsold' || s === 'closed') return 'closed';
  if (s === 'open' || s === 'published') return 'active';
  return 'no-bids';
}

// ── Campaign placeholder ──────────────────────────────────────────────
// Prototype renders a CRM campaign chip when auction.campaignId is set.
// Eng's wire doesn't surface campaigns on AuctionEvent. Stub the lookup
// so call sites compile; resolves to undefined → UI hides the chip.
export interface PrototypeCampaign {
  id: string;
  name: string;
  type: 'Annual' | 'Event' | 'Stewardship';
  raised: string;
  donors: number;
  last: string;
}

export function findCampaign(_id: string | null | undefined): PrototypeCampaign | undefined {
  return undefined;
}

// ── Bidder URL builder ───────────────────────────────────────────────
import { env } from '../env';
import { getOrgSlug } from './org';

export function bidderBaseUrl(): string {
  const slug = getOrgSlug();
  if (slug) {
    const { protocol, hostname } = window.location;
    const bidderHost = hostname.replace(/\.admin\./, '.app.');
    const bidderPort = new URL(env.bidderBaseUrl).port;
    const portSuffix = bidderPort ? `:${bidderPort}` : '';
    return `${protocol}//${bidderHost}${portSuffix}`;
  }
  return env.bidderBaseUrl;
}

// The bidder app namespaces everything under the event slug at the root
// (`/:slug/items/:id`) — there is no `/auctions` segment in its router, so
// prefixing one produces a link that 404s for whoever the admin sends it to.
export function bidderItemUrl(eventSlug: string, itemId: number): string {
  return `${bidderBaseUrl()}/${eventSlug}/items/${itemId}`;
}

export function bidderBundleUrl(eventSlug: string, bundleId: number): string {
  return `${bidderBaseUrl()}/${eventSlug}/bundles/${bundleId}`;
}
